import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  fetchCompetingAgents,
  createCompetingAgent,
  updateCompetingAgent,
  deleteCompetingAgent,
  fetchAgentStatistics,
} from "../api/competingAgentApi";

export const useCompetingAgents = () => {
  const queryClient = useQueryClient();

  // Fetch all competing agents
  const {
    data: agents,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["competingAgents"],
    queryFn: fetchCompetingAgents,
  });

  // Create competing agent
  const createAgentMutation = useMutation({
    mutationFn: createCompetingAgent,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["competingAgents"] });
      toast.success(data.message || "Competing agent created successfully!");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create competing agent"
      );
    },
  });

  // Update competing agent
  const updateAgentMutation = useMutation({
    mutationFn: ({ id, agentData }) => updateCompetingAgent(id, agentData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["competingAgents"] });
      toast.success(data.message || "Competing agent updated successfully!");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update competing agent"
      );
    },
  });

  // Delete competing agent
  const deleteAgentMutation = useMutation({
    mutationFn: deleteCompetingAgent,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["competingAgents"] });
      toast.success(data.message || "Competing agent deleted successfully!");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete competing agent"
      );
    },
  });

  // Fetch agent statistics
  const { data: statistics, isLoading: statsLoading } = useQuery({
    queryKey: ["agentStatistics"],
    queryFn: fetchAgentStatistics,
  });

  return {
    agents: Array.isArray(agents) ? agents : [],
    isLoading,
    error,
    statistics,
    statsLoading,
    createAgent: createAgentMutation.mutate,
    createAgentLoading: createAgentMutation.isPending,
    createAgentSuccess: createAgentMutation.isSuccess,
    updateAgent: updateAgentMutation.mutate,
    updateAgentLoading: updateAgentMutation.isPending,
    updateAgentSuccess: updateAgentMutation.isSuccess,
    deleteAgent: deleteAgentMutation.mutate,
    deleteAgentLoading: deleteAgentMutation.isPending,
    deleteAgentSuccess: deleteAgentMutation.isSuccess,
  };
};
